// Copyright 2024 Sorama B.V.
import { SpiderChartFactory } from "../Layout/SpiderChartFactory";
import {
    AllPsychoAcousticMetricTypes,
    MetricRanges,
    PsychoAcousticAnalysisResult,
    PsychoAcousticMetricType,
    SpiderChart,
} from "../SpiderChart";

export class SpiderChartZoomManager {
    private originalNormalizedValues: { [measurementId: string]: PsychoAcousticAnalysisResult } = {};
    private currentNormalizedValues: { [measurementId: string]: PsychoAcousticAnalysisResult } = {};
    private hiddenNormalizedValues: { [measurementId: string]: PsychoAcousticAnalysisResult } = {};
    private readonly baseZoomStep = 0.01;

    // Spider chart lines
    private spiderLinesCurrentZoomLevel = 0.0;
    private readonly spiderLinesZoomStep = 0.05;

    public hasPolygonStroke: { [measurementId: string]: boolean } = {};
    public currentRanges: MetricRanges | null = null;

    constructor(private spiderChart: SpiderChart) {}

    /**
     * Handle zooming on a specific metric based on scroll direction.
     * @param zoomDirection - "zoomIn" or "zoomOut"
     */
    public handleZoomOnMetric(zoomDirection: string) {
        // Adjust zoom level based on the zoom direction
        if (zoomDirection === "zoomIn") {
            this.spiderLinesCurrentZoomLevel += this.spiderLinesZoomStep;
        } else if (zoomDirection === "zoomOut") {
            this.spiderLinesCurrentZoomLevel -= this.spiderLinesZoomStep;
        }

        // Update the polygon lines in the SVG with the new zoom level
        SpiderChartFactory.updatePolygonLines(
            this.spiderChart.svgManager.svg,
            this.spiderLinesCurrentZoomLevel,
            SpiderChartFactory.OUTER_RADIUS
        );

        // Iterate over all metrics and calculate dynamic zoom step based on their normalized values
        this.spiderChart.measurements.forEach((measurement) => {
            const measurementId = measurement.id;

            // Ensure original normalized values are initialized for this measurement
            this.storeOriginalNormalizedValues(measurementId);

            // Initialize currentNormalizedValues if not already present
            if (!this.currentNormalizedValues[measurementId]) {
                this.currentNormalizedValues[measurementId] = { ...this.originalNormalizedValues[measurementId] };
            }

            // Retrieve the current normalized values for this measurement
            const normalizedValues = this.currentNormalizedValues[measurementId];

            // Apply the zoom logic based on the normalized values
            for (const metricKey of AllPsychoAcousticMetricTypes) {
                let normalizedValue = normalizedValues[metricKey]; // Fetch the current normalized value

                // If the normalized value is 0, skip zooming for this metric
                if (normalizedValue == 0.0) continue;

                // Calculate zoom step based on current normalized value (higher value gets bigger step)
                const zoomStep = this.calculateZoomStepForNormalizedValue(normalizedValue);

                // Update the normalized value based on zoom direction
                const newNormalizedValue = this.updateZoomedNormalizedValue(
                    measurementId,
                    metricKey,
                    zoomDirection,
                    normalizedValue,
                    zoomStep
                );

                // Set the updated normalized value back into the currentNormalizedValues object for subsequent use
                this.currentNormalizedValues[measurementId][metricKey] = newNormalizedValue;
            }
        });

        // Re-plot the values after zooming
        const results = this.spiderChart.measurements.map((m) => this.spiderChart.computedResults[m.id]);
        this.normalizeAndPlotValuesWithUpdatedNormalizedValues(results);
    }

    /**
     * Resets the zooming for all measurements.
     * Clears the normalized values and stores them again based on the original ranges.
     */
    public resetZooming(): void {
        this.hasPolygonStroke = {};
        this.originalNormalizedValues = {};
        this.currentNormalizedValues = {};
        this.hiddenNormalizedValues = {};
        this.spiderLinesCurrentZoomLevel = 0;
        // Update the polygon lines in the SVG with the new zoom level
        SpiderChartFactory.updatePolygonLines(
            this.spiderChart.svgManager.svg,
            this.spiderLinesCurrentZoomLevel,
            SpiderChartFactory.OUTER_RADIUS
        );
    }

    /**
     * Calculates the zoom step based on the normalized value.
     * @param normalizedValue - The normalized value between 0 and 1.
     * @returns The calculated zoom step.
     */
    private calculateZoomStepForNormalizedValue(normalizedValue: number): number {
        const scalingFactor = 0.02; // Factor to scale the zoom step based on the normalized value
        const exponent = 7;

        // Higher normalized value gets a larger zoom step
        return this.baseZoomStep + Math.pow(normalizedValue, exponent) * scalingFactor;
    }

    /**
     * Checks whether normalized values exist for the given measurementId.
     * If they don't exist, the function initializes them.
     * @param measurementId - The ID of the measurement to check and initialize.
     */
    private storeOriginalNormalizedValues(measurementId: string) {
        // Initialize originalNormalizedValues object if it's null or undefined
        if (!this.originalNormalizedValues) {
            this.originalNormalizedValues = {};
        }

        // Checks and adds if the normalized values for the given measurementId are not present
        if (!this.originalNormalizedValues[measurementId]) {
            this.originalNormalizedValues[measurementId] = {} as PsychoAcousticAnalysisResult;

            // Get the computed results for the given measurement ID
            const result = this.spiderChart.computedResults[measurementId];

            // Normalize the values for all metrics in one go
            const normalizedValues = this.spiderChart.normalizeValues(result, this.currentRanges);

            // Store the normalized values for each metric
            for (const metricType of AllPsychoAcousticMetricTypes) {
                this.originalNormalizedValues[measurementId][metricType] = normalizedValues[metricType];
            }
        }
    }

    /**
     * Updates the normalized value of a metric during zooming.
     * @param measurementId - The ID of the current measurement.
     * @param metricKey - The key of the metric being zoomed (e.g., Loudness, Sharpness).
     * @param zoomDirection - "zoomIn" or "zoomOut".
     * @param normalizedValue - The current normalized value.
     * @param zoomStep - The calculated zoom step.
     * @returns The updated normalized value after zooming.
     */
    private updateZoomedNormalizedValue(
        measurementId: string,
        metricType: PsychoAcousticMetricType,
        zoomDirection: string,
        normalizedValue: number,
        zoomStep: number
    ): number {
        const upperCap = 1;
        const downCap = 0.05;

        // Initialize hiddenNormalizedValues if necessary
        if (!this.hiddenNormalizedValues[measurementId]) {
            this.hiddenNormalizedValues[measurementId] = {} as PsychoAcousticAnalysisResult;
        }

        let newNormalizedValue = normalizedValue; // Start with the current normalized value

        // Retrieve the hidden value if it exists, otherwise use the current normalized value
        let hiddenValue = this.hiddenNormalizedValues[measurementId][metricType] || newNormalizedValue;

        if (zoomDirection === "zoomIn") {
            hiddenValue += zoomStep;

            // If hidden value exceeds the upper cap, store it but cap the displayed value at 1
            if (hiddenValue > upperCap) {
                this.hiddenNormalizedValues[measurementId][metricType] = hiddenValue; // Store the hidden value
                newNormalizedValue = upperCap; // Cap the display at 1
                this.hasPolygonStroke[measurementId] = false;
            } else if (hiddenValue < downCap) {
                this.hiddenNormalizedValues[measurementId][metricType] = hiddenValue;
            } else {
                // If hidden value is within bounds, stop using the hidden value and switch back to normalized value
                newNormalizedValue = hiddenValue;

                this.hasPolygonStroke[measurementId] = true;
                delete this.hiddenNormalizedValues[measurementId][metricType]; // Clear hidden value if back within bounds
            }
        } else if (zoomDirection === "zoomOut") {
            hiddenValue -= zoomStep;

            // If hidden value falls below the lower cap, store it but cap the displayed value at 0.01
            if (hiddenValue < downCap) {
                this.hiddenNormalizedValues[measurementId][metricType] = hiddenValue; // Store the hidden value
                newNormalizedValue = downCap; // Cap the display at 0.01
                this.hasPolygonStroke[measurementId] = false;
            } else if (hiddenValue > upperCap) {
                this.hiddenNormalizedValues[measurementId][metricType] = hiddenValue;
            } else {
                // If hidden value is within bounds, stop using the hidden value and switch back to normalized value
                newNormalizedValue = hiddenValue;
                this.hasPolygonStroke[measurementId] = true;
                delete this.hiddenNormalizedValues[measurementId][metricType]; // Clear hidden value if back within bounds
            }
        }

        return newNormalizedValue;
    }

    /**
     * Updates the normalized values in the measurements and re-plots the chart.
     * @param result - Array of PsychoAcousticsResults containing the data to be plotted.
     */
    private normalizeAndPlotValuesWithUpdatedNormalizedValues(result: PsychoAcousticAnalysisResult[]) {
        if (result.length === 0) return;

        // Iterate over all results and apply the updated normalized values directly
        result.forEach((_, i) => {
            const measurement = this.spiderChart.measurements[i];

            // Fetch the current normalized values for this measurement
            const normalizedValues = this.currentNormalizedValues[measurement.id];

            // Pass the updated normalized values to the spider chart
            this.spiderChart.addMeasurementToSpiderChart(this.spiderChart.measurements[i], normalizedValues);
        });
    }
}
